// Utility to manage affiliate signup progress in localStorage

const SIGNUP_PROGRESS_KEY = 'affiliate_signup_progress';

export const SignupStep = {
  FORM: 'form',
  VERIFICATION: 'verification',
  TERMS: 'terms',
  PROFILE: 'profile',
  SAMPLE_VIDEOS: 'sample_videos',
  COMPLETE: 'complete'
};

/**
 * Save signup progress to localStorage
 * @param {string} step - Current step in signup flow
 * @param {object} data - Data to save (formData, email, etc.)
 */
export const saveSignupProgress = (step, data = {}) => {
  try {
    const progress = {
      step,
      data,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(SIGNUP_PROGRESS_KEY, JSON.stringify(progress));
  } catch (error) {
    console.error('Error saving signup progress:', error);
  }
};

/**
 * Get saved signup progress from localStorage
 * @returns {object|null} Progress object or null if not found
 */
export const getSignupProgress = () => {
  try {
    const progress = localStorage.getItem(SIGNUP_PROGRESS_KEY);
    if (!progress) return null;
    
    const parsed = JSON.parse(progress);
    
    // Check if progress is older than 24 hours (optional: expire old progress)
    const timestamp = new Date(parsed.timestamp);
    const now = new Date();
    const hoursDiff = (now - timestamp) / (1000 * 60 * 60);
    
    if (hoursDiff > 24) {
      clearSignupProgress();
      return null;
    }
    
    return parsed;
  } catch (error) {
    console.error('Error getting signup progress:', error);
    return null;
  }
};

/**
 * Clear signup progress from localStorage
 */
export const clearSignupProgress = () => {
  try {
    localStorage.removeItem(SIGNUP_PROGRESS_KEY);
  } catch (error) {
    console.error('Error clearing signup progress:', error);
  }
};

/**
 * Check if user should be redirected to a specific step
 * @param {string} currentPath - Current route path
 * @returns {object|null} Redirect info or null
 */
export const shouldRedirectToProgress = (currentPath) => {
  const progress = getSignupProgress();
  if (!progress) return null;
  
  // Don't redirect if already on the correct step or a later step
  const stepOrder = {
    [SignupStep.FORM]: 1,
    [SignupStep.VERIFICATION]: 2,
    [SignupStep.TERMS]: 3,
    [SignupStep.PROFILE]: 4,
    [SignupStep.SAMPLE_VIDEOS]: 5,
    [SignupStep.COMPLETE]: 6
  };
  
  const currentStepOrder = stepOrder[progress.step] || 0;
  
  // Map paths to step orders
  const pathStepMap = {
    '/signup': 1,
    '/verify-email': 2,
    '/terms-agreement': 3,
    '/user-profile': 4,
    '/sample-videos': 5,
    '/thank-you': 6
  };
  
  const currentPathOrder = pathStepMap[currentPath] || 0;
  
  // If we're on an earlier step than saved progress, redirect
  if (currentPathOrder < currentStepOrder) {
    return {
      step: progress.step,
      data: progress.data,
      redirectPath: getPathForStep(progress.step)
    };
  }
  
  return null;
};

/**
 * Get the route path for a given step
 * @param {string} step - Signup step
 * @returns {string} Route path
 */
export const getPathForStep = (step) => {
  const stepPathMap = {
    [SignupStep.FORM]: '/signup',
    [SignupStep.VERIFICATION]: '/signup',
    [SignupStep.TERMS]: '/terms-agreement',
    [SignupStep.PROFILE]: '/user-profile',
    [SignupStep.SAMPLE_VIDEOS]: '/sample-videos',
    [SignupStep.COMPLETE]: '/thank-you'
  };
  
  return stepPathMap[step] || '/signup';
};

